import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Chip,
    Stack,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Avatar,
    TextField,
    InputAdornment,
    Divider,
    Tooltip,
    Fade,
} from '@mui/material';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from 'recharts';
import {
    People,
    TrendingUp,
    Visibility,
    Close,
    CheckCircle,
    Cancel,
    ArrowUpward,
    ArrowDownward,
    UnfoldMore,
    Search,
    Download,
    Clear,
    FileDownload,
} from '@mui/icons-material';

const AssessmentScoreCharts = ({ filteredData, allUsers, allProgramCodes, selectedTab, onExportIndividualPDF }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
    const [searchTerm, setSearchTerm] = useState('');
    
    // Helper function to get max score from assessment JSON or default to 60
    const getMaxScore = (assessment) => {
        if (assessment?.json) {
            try {
                const jsonData = typeof assessment.json === 'string' ? JSON.parse(assessment.json) : assessment.json;
                return jsonData.max_score || 60;
            } catch (error) {
                console.log('Error parsing JSON for max_score:', error);
                return 60;
            }
        }
        return assessment?.max_score || 60;
    };
    
    const getAssessmentAvgScore = (assessment) => {
        if (assessment.assessmentResults.length === 0) return 0;
        const totalScore = assessment.assessmentResults.reduce((sum, user) => sum + user.json.finalScore, 0);
        return totalScore / assessment.assessmentResults.length;
    };

    const handleViewDetails = (assessment) => {
        setSelectedAssessment(assessment);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setSelectedAssessment(null);
    };

    const handleExportIndividualPDF = (assessment) => {
        if (onExportIndividualPDF) {
            onExportIndividualPDF(assessment);
        }
    };

    // Get users for the selected program code and their assessment status
    const getUserAssessmentStatus = () => {
        if (!selectedAssessment || !allUsers || !allProgramCodes) return [];
        
        const currentProgramCode = allProgramCodes[selectedTab];
        if (!currentProgramCode) return [];
        
        // Filter users by current program code and role
        const programUsers = allUsers.filter(user => 
            user.programCode.includes(currentProgramCode) && user.role === "User"
        );
        
        // Check submission status for each user
        return programUsers.map(user => {
            const hasSubmitted = selectedAssessment.assessmentResults.some(result => 
                result.json?.user_details?.email === user.email ||
                result.user_id === user.id ||
                result.user_id === user.email
            );
            
            const submissionData = hasSubmitted 
                ? selectedAssessment.assessmentResults.find(result => 
                    result.json?.user_details?.email === user.email ||
                    result.user_id === user.id ||
                    result.user_id === user.email
                  )
                : null;
            
            return {
                ...user,
                hasSubmitted,
                score: submissionData?.json?.finalScore || null,
                maxScore: getMaxScore(selectedAssessment),
                level: submissionData?.json?.finalInterpretation?.name || null
            };
        });
    };

    const userAssessmentStatus = getUserAssessmentStatus();

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        } else if (sortConfig.key === key && sortConfig.direction === 'desc') {
            direction = null;
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = () => {
        if (!sortConfig.key || !sortConfig.direction) {
            return userAssessmentStatus;
        }

        return [...userAssessmentStatus].sort((a, b) => {
            let aValue, bValue;
            
            switch (sortConfig.key) {
                case 'name':
                    aValue = (a.name || '').toLowerCase();
                    bValue = (b.name || '').toLowerCase();
                    break;
                case 'status':
                    aValue = a.hasSubmitted ? 1 : 0;
                    bValue = b.hasSubmitted ? 1 : 0;
                    break;
                case 'score':
                    aValue = a.score || 0;
                    bValue = b.score || 0;
                    break;
                case 'level':
                    aValue = a.level || '';
                    bValue = b.level || '';
                    break;
                case 'email':
                    aValue = (a.email || '').toLowerCase();
                    bValue = (b.email || '').toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <UnfoldMore sx={{ fontSize: 14, opacity: 0.5 }} />;
        }
        if (sortConfig.direction === 'asc') {
            return <ArrowUpward sx={{ fontSize: 14, color: '#0a0068' }} />;
        }
        if (sortConfig.direction === 'desc') {
            return <ArrowDownward sx={{ fontSize: 14, color: '#0a0068' }} />;
        }
        return <UnfoldMore sx={{ fontSize: 14, opacity: 0.5 }} />;
    };

    const sortedData = getSortedData();

    // Filter data based on search term
    const filteredAndSortedData = sortedData.filter(user => {
        if (!searchTerm) return true;
        const searchLower = searchTerm.toLowerCase();
        return (
            (user.name || '').toLowerCase().includes(searchLower) ||
            (user.email || '').toLowerCase().includes(searchLower) ||
            (user.level || '').toLowerCase().includes(searchLower) ||
            (user.hasSubmitted ? 'submitted' : 'not submitted').includes(searchLower)
        );
    });

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleExportToExcel = () => {
        if (!selectedAssessment || filteredAndSortedData.length === 0) return;
        
        // Create workbook data
        const headers = ['Name', 'Email', 'Status', 'Score', 'Max Score', 'Percentage', 'Performance Level', 'Program Code', 'Assessment Name'];
        
        // Convert data to CSV format first (which Excel can open)
        const csvData = [
            headers,
            ...filteredAndSortedData.map(user => [
                user.name || 'Unknown',
                user.email || '',
                user.hasSubmitted ? 'Submitted' : 'Not Submitted',
                user.score || 0,
                user.maxScore || 0,
                user.hasSubmitted ? `${Math.round((user.score / user.maxScore) * 100)}%` : '0%',
                user.level ? user.level.replace(/[🌟💎⚙️🛠🔴]/g, '').trim() : '',
                allProgramCodes?.[selectedTab] || 'All',
                selectedAssessment.assignment_name
            ])
        ];
        
        // Convert to CSV string
        const csvContent = csvData.map(row => 
            row.map(cell => {
                // Escape quotes and wrap in quotes if necessary
                const cellValue = String(cell || '');
                if (cellValue.includes(',') || cellValue.includes('"') || cellValue.includes('\n')) {
                    return `"${cellValue.replace(/"/g, '""')}"`;
                }
                return cellValue;
            }).join(',')
        ).join('\n');
        
        // Add BOM for UTF-8 to ensure proper character encoding in Excel
        const BOM = '\uFEFF';
        const csvWithBOM = BOM + csvContent;
        
        // Create and download file
        const blob = new Blob([csvWithBOM], { 
            type: 'text/csv;charset=utf-8;' 
        });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${selectedAssessment.assignment_name}_${allProgramCodes?.[selectedTab] || 'All'}_Assessment_Report.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    return (
        <Box sx={{
            px: 3,
            mb: 4,
            '@media print': {
                pageBreakInside: 'avoid',
                breakInside: 'avoid',
                mb: 2,
                px: 0,
                '& *': {
                    '-webkit-print-color-adjust': 'exact !important',
                    'color-adjust': 'exact !important',
                    'print-color-adjust': 'exact !important'
                }
            }
        }}>
            <Typography variant="h6" component="div" fontWeight="bold" sx={{ mb: 2, fontSize: '0.95rem' }} color="#0a0068">
                Assessment Score Analysis
            </Typography>
            <Grid container spacing={1.5}>
                {filteredData.map((assessment, index) => {
                    // Check if assessment has 20 or more participants/bars
                    const hasMany = assessment.assessmentResults.length >= 20;
                    
                    return (
                        <Grid 
                            item 
                            xs={12} 
                            lg={hasMany ? 12 : 6} 
                            key={assessment.id}
                            sx={{
                                '@media print': {
                                    pageBreakInside: 'avoid',
                                    breakInside: 'avoid'
                                }
                            }}
                        >
                        <Card 
                            elevation={2} 
                            sx={{ 
                                height: hasMany ? 400 : 320, 
                                borderRadius: 2,
                                '@media print': {
                                    height: 'auto',
                                    minHeight: '300px',
                                    boxShadow: 'none',
                                    border: '1px solid #ddd',
                                    pageBreakInside: 'avoid',
                                    breakInside: 'avoid'
                                }
                            }}
                        >
                            <CardContent sx={{ 
                                height: '100%', 
                                display: 'flex', 
                                flexDirection: 'column',
                                p: 2,
                                '&:last-child': { pb: 2 },
                                '@media print': {
                                    height: 'auto'
                                }
                            }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="h6" component="div" fontWeight="bold" color="#0a0068" sx={{ fontSize: '0.9rem' }}>
                                        {assessment.assignment_name}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: '#0a0068',
                                                backgroundColor: 'rgba(10, 0, 104, 0.05)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(10, 0, 104, 0.1)',
                                                },
                                                width: 28,
                                                height: 28
                                            }}
                                            title="View Details"
                                            onClick={() => handleViewDetails(assessment)}
                                        >
                                            <Visibility sx={{ fontSize: 16 }} />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            sx={{
                                                color: '#cc4049',
                                                backgroundColor: 'rgba(204, 64, 73, 0.05)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(204, 64, 73, 0.1)',
                                                },
                                                width: 28,
                                                height: 28
                                            }}
                                            title="Export Individual PDF"
                                            onClick={() => handleExportIndividualPDF(assessment)}
                                        >
                                            <Download sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </Box>
                                    {/* <Chip
                                        label={assessment.assignment_type}
                                        color="primary"
                                        size="small"
                                        sx={{ fontSize: '0.75rem', height: 22 }}
                                        variant="outlined"
                                        sx={{
                                            '@media print': {
                                                border: '1px solid #0a0068',
                                                color: '#0a0068',
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                    /> */}
                                </Box>

                                <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
                                    <Chip
                                        label={`${assessment.assessmentResults.length} Participants`}
                                        variant="filled"
                                        size="small"
                                        icon={<People sx={{ fontSize: '14px !important' }} />}
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            height: 26,
                                            fontWeight: 600,
                                            backgroundColor: 'rgba(10, 0, 104, 0.1)',
                                            color: '#0a0068',
                                            border: '1px solid rgba(10, 0, 104, 0.2)',
                                            borderRadius: '6px',
                                            '& .MuiChip-icon': {
                                                color: '#0a0068'
                                            },
                                            '@media print': {
                                                border: '1px solid #666',
                                                color: '#666',
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                    />
                                    <Chip
                                        label={`Avg: ${Math.round(getAssessmentAvgScore(assessment))}/${getMaxScore(assessment)}`}
                                        variant="filled"
                                        size="small"
                                        icon={<TrendingUp sx={{ fontSize: '14px !important' }} />}
                                        sx={{ 
                                            fontSize: '0.75rem', 
                                            height: 26,
                                            fontWeight: 600,
                                            backgroundColor: 'rgba(76, 175, 80, 0.15)',
                                            color: '#2e7d32',
                                            border: '1px solid rgba(76, 175, 80, 0.3)',
                                            borderRadius: '6px',
                                            '& .MuiChip-icon': {
                                                color: '#2e7d32'
                                            },
                                            '@media print': {
                                                border: '1px solid #2e7d32',
                                                color: '#2e7d32',
                                                backgroundColor: 'transparent'
                                            }
                                        }}
                                    />
                                </Stack>

                                <Box sx={{ 
                                    flexGrow: 1, 
                                    minHeight: 0,
                                    // '@media print': {
                                    //     height: '400px',
                                    //     width: '100%',
                                    //     '& .recharts-wrapper': {
                                    //         '-webkit-print-color-adjust': 'exact !important',
                                    //         'color-adjust': 'exact !important',
                                    //         'print-color-adjust': 'exact !important',
                                    //         width: '100% !important',
                                    //         height: '100% !important'
                                    //     },
                                    //     '& svg': {
                                    //         '-webkit-print-color-adjust': 'exact !important',
                                    //         'color-adjust': 'exact !important',
                                    //         'print-color-adjust': 'exact !important',
                                    //         width: '100% !important',
                                    //         height: '100% !important'
                                    //     },
                                    //     '& .recharts-surface': {
                                    //         '-webkit-print-color-adjust': 'exact !important',
                                    //         'color-adjust': 'exact !important',
                                    //         'print-color-adjust': 'exact !important'
                                    //     }
                                    // }
                                }}>
                                    <div style={{ 
                                        width: '100%', 
                                        height: '100%',
                                        printColorAdjust: 'exact',
                                        WebkitPrintColorAdjust: 'exact',
                                        colorAdjust: 'exact'
                                    }}>
                                        <ResponsiveContainer 
                                            width="100%" 
                                            height="100%"
                                            style={{
                                                printColorAdjust: 'exact',
                                                WebkitPrintColorAdjust: 'exact',
                                                colorAdjust: 'exact'
                                            }}
                                        >
                                            <BarChart
                                                width={400}
                                                height={250}
                                                data={assessment.assessmentResults.map(user => {
                                                    const fullName = `${user.json.user_details.fname} ${user.json.user_details.lname}`.trim() || 'User';
                                                    const truncatedName = fullName.length > 12 ? `${fullName.substring(0, 12)}...` : fullName;
                                                    return {
                                                        name: truncatedName,
                                                        fullName: fullName,
                                                        score: user.json.finalScore,
                                                        maxScore: getMaxScore(assessment),
                                                        level: user.json.finalInterpretation.name,
                                                        email: user.json.user_details.email,
                                                        fill: "#0a0068"
                                                    };
                                                })}
                                                margin={{ top: 10, right: 20, left: 10, bottom: 50 }}
                                                style={{
                                                    printColorAdjust: 'exact',
                                                    WebkitPrintColorAdjust: 'exact',
                                                    colorAdjust: 'exact'
                                                }}
                                            >
                                                {/* <CartesianGrid 
                                                    strokeDasharray="3 3" 
                                                    stroke="#f0f0f0"
                                                    style={{
                                                        printColorAdjust: 'exact',
                                                        WebkitPrintColorAdjust: 'exact'
                                                    }}
                                                /> */}
                                                <XAxis
                                                    dataKey="name"
                                                    angle={-45}
                                                    textAnchor="end"
                                                    height={70}
                                                    fontSize={10}
                                                    stroke="#666"
                                                    style={{
                                                        printColorAdjust: 'exact',
                                                        WebkitPrintColorAdjust: 'exact'
                                                    }}
                                                />
                                                <YAxis
                                                    domain={[0, getMaxScore(assessment)]}
                                                    fontSize={10}
                                                    stroke="#666"
                                                    style={{
                                                        printColorAdjust: 'exact',
                                                        WebkitPrintColorAdjust: 'exact'
                                                    }}
                                                />
                                                <RechartsTooltip
                                                    formatter={(value, name, props) => [
                                                        [`Score: ${value}/${props.payload.maxScore}`, ''],
                                                        [`Level: ${props.payload.level}`, ''],
                                                        [`Email: ${props.payload.email}`, '']
                                                    ]}
                                                    labelFormatter={(label, payload) => {
                                                        const data = payload?.[0]?.payload;
                                                        return data?.fullName || label;
                                                    }}
                                                    labelStyle={{ color: '#333', fontWeight: 'bold' }}
                                                    contentStyle={{
                                                        backgroundColor: '#fff',
                                                        border: '1px solid #ccc',
                                                        borderRadius: '8px',
                                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                                                    }}
                                                />
                                                <Bar
                                                    dataKey="score"
                                                    name="Final Score"
                                                    radius={[4, 4, 0, 0]}
                                                    stroke="#fff"
                                                    strokeWidth={1}
                                                    fill="#0a0068"
                                                    // style={{
                                                    //     printColorAdjust: 'exact',
                                                    //     WebkitPrintColorAdjust: 'exact'
                                                    // }}
                                                />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </div>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    );
                })}
            </Grid>

            {/* Assessment Details Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 2 }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: '#0a0068',
                    color: 'white',
                    fontSize: '1.1rem'
                }}>
                    <Box>
                        <Typography variant="h6" component="div" sx={{ fontSize: '1.1rem' }}>
                            Assessment Submission Status
                        </Typography>
                        {selectedAssessment && (
                            <Typography variant="subtitle2" sx={{ opacity: 0.9, fontSize: '0.85rem' }}>
                                {selectedAssessment.assignment_name} - {allProgramCodes?.[selectedTab] || 'All Programs'}
                            </Typography>
                        )}
                    </Box>
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={handleCloseDialog}
                        aria-label="close"
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    {/* Enhanced Search and Export Section */}
                    <Box sx={{ 
                        p: 3, 
                        pb: 2,
                        background: 'linear-gradient(135deg, rgba(10, 0, 104, 0.02) 0%, rgba(204, 64, 73, 0.02) 100%)',
                        borderBottom: '1px solid rgba(10, 0, 104, 0.1)'
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 3 }}>
                            {/* Search Section */}
                            <Box sx={{ flexGrow: 1, maxWidth: '500px' }}>
                                {/* <Typography variant="body2" sx={{ 
                                    fontSize: '0.75rem', 
                                    fontWeight: 600, 
                                    color: '#495057', 
                                    mb: 1,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Search Users
                                </Typography> */}
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search by name, email, status, or performance level..."
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 2,
                                            backgroundColor: 'white',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                            },
                                            '&.Mui-focused': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 16px rgba(10, 0, 104, 0.3)',
                                                borderColor: '#0a0068',
                                                '& fieldset': {
                                                    borderColor: '#0a0068 !important'
                                                }
                                            }
                                        },
                                        '& .MuiInputBase-input': {
                                            fontSize: '0.85rem',
                                            fontWeight: 500
                                        }
                                    }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Search sx={{ 
                                                    fontSize: 18, 
                                                    color: searchTerm ? '#0a0068' : 'text.secondary',
                                                    transition: 'color 0.2s ease'
                                                }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: searchTerm && (
                                            <InputAdornment position="end">
                                                <Tooltip title="Clear search">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => setSearchTerm('')}
                                                        sx={{ 
                                                            width: 24, 
                                                            height: 24,
                                                            color: '#cc4049',
                                                            '&:hover': {
                                                                color: '#cc4049',
                                                                backgroundColor: 'rgba(204, 64, 73, 0.1)',
                                                                transform: 'scale(1.1)'
                                                            }
                                                        }}
                                                    >
                                                        <Clear sx={{ fontSize: 14 }} />
                                                    </IconButton>
                                                </Tooltip>
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                {searchTerm && (
                                    <Fade in={!!searchTerm}>
                                        <Typography variant="caption" sx={{ 
                                            fontSize: '0.7rem', 
                                            color: '#6c757d', 
                                            mt: 0.5,
                                            display: 'block'
                                        }}>
                                            Showing {filteredAndSortedData.length} of {userAssessmentStatus.length} users
                                        </Typography>
                                    </Fade>
                                )}
                            </Box>
                            
                            {/* Export Section */}
                            <Box sx={{ textAlign: 'right', minWidth: '140px' }}>
                                {/* <Typography variant="body2" sx={{ 
                                    fontSize: '0.75rem', 
                                    fontWeight: 600, 
                                    color: '#495057', 
                                    mb: 1,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Export Data
                                </Typography> */}
                                <Tooltip title={filteredAndSortedData.length === 0 ? 'No data to export' : `Export ${filteredAndSortedData.length} users to CSV (opens in Excel)`}>
                                    <span>
                                        <Button
                                            variant="contained"
                                            size="medium"
                                            startIcon={<FileDownload sx={{ fontSize: 18 }} />}
                                            onClick={handleExportToExcel}
                                            disabled={filteredAndSortedData.length === 0}
                                            sx={{
                                                textTransform: 'none',
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                borderRadius: 2,
                                                px: 2.5,
                                                py: 1,
                                                background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                                                boxShadow: '0 2px 8px rgba(10, 0, 104, 0.3)',
                                                transition: 'all 0.2s ease',
                                                '&:hover': {
                                                    background: 'linear-gradient(135deg, rgba(10, 0, 104, 0.9) 0%, rgba(204, 64, 73, 0.9) 100%)',
                                                    transform: 'translateY(-2px)',
                                                    boxShadow: '0 4px 16px rgba(10, 0, 104, 0.4)'
                                                },
                                                '&:active': {
                                                    transform: 'translateY(0px)'
                                                },
                                                '&:disabled': {
                                                    background: 'rgba(10, 0, 104, 0.1)',
                                                    color: 'rgba(10, 0, 104, 0.4)',
                                                    boxShadow: 'none',
                                                    transform: 'none'
                                                }
                                            }}
                                        >
                                            Export CSV
                                        </Button>
                                    </span>
                                </Tooltip>
                                {/* <Typography variant="caption" sx={{ 
                                    fontSize: '0.65rem', 
                                    color: '#6c757d', 
                                    mt: 0.5,
                                    display: 'block',
                                    lineHeight: 1.2
                                }}>
                                    {filteredAndSortedData.length} records
                                </Typography> */}
                            </Box>
                        </Box>
                    </Box>
                    
                    {/* Table Container */}
                    <Box sx={{ p: 2 }}>
                    <TableContainer component={Paper} elevation={1}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell 
                                        sx={{ 
                                            fontWeight: 600, 
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': { bgcolor: '#eeeeee' }
                                        }}
                                        onClick={() => handleSort('name')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            User
                                            {getSortIcon('name')}
                                        </Box>
                                    </TableCell>
                                    <TableCell 
                                        align="center" 
                                        sx={{ 
                                            fontWeight: 600, 
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': { bgcolor: '#eeeeee' }
                                        }}
                                        onClick={() => handleSort('status')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            Status
                                            {getSortIcon('status')}
                                        </Box>
                                    </TableCell>
                                    <TableCell 
                                        align="center" 
                                        sx={{ 
                                            fontWeight: 600, 
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': { bgcolor: '#eeeeee' }
                                        }}
                                        onClick={() => handleSort('score')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            Score
                                            {getSortIcon('score')}
                                        </Box>
                                    </TableCell>
                                    <TableCell 
                                        align="center" 
                                        sx={{ 
                                            fontWeight: 600, 
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': { bgcolor: '#eeeeee' }
                                        }}
                                        onClick={() => handleSort('level')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                            Performance Level
                                            {getSortIcon('level')}
                                        </Box>
                                    </TableCell>
                                    <TableCell 
                                        sx={{ 
                                            fontWeight: 600, 
                                            fontSize: '0.8rem',
                                            cursor: 'pointer',
                                            userSelect: 'none',
                                            '&:hover': { bgcolor: '#eeeeee' }
                                        }}
                                        onClick={() => handleSort('email')}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            Email
                                            {getSortIcon('email')}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredAndSortedData.map((user, index) => (
                                    <TableRow
                                        key={user.id}
                                        sx={{
                                            '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                                            '&:hover': { bgcolor: '#f0f0f0' }
                                        }}
                                    >
                                        <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar
                                                    sx={{
                                                        width: 24,
                                                        height: 24,
                                                        fontSize: '0.7rem',
                                                        bgcolor: user.hasSubmitted ? '#4caf50' : '#f44336'
                                                    }}
                                                >
                                                    {user.name?.charAt(0) || user.email?.charAt(0) || 'U'}
                                                </Avatar>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    {user.name || 'Unknown'}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                icon={user.hasSubmitted ? <CheckCircle /> : <Cancel />}
                                                label={user.hasSubmitted ? 'Submitted' : 'Not Submitted'}
                                                color={user.hasSubmitted ? 'success' : 'error'}
                                                size="small"
                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                {user.hasSubmitted ? `${user.score}/${user.maxScore}` : '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            {user.hasSubmitted && user.level ? (
                                                <Chip
                                                    label={user.level.replace(/[🌟💎⚙️🛠🔴]/g, '').trim()}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        height: 20,
                                                        bgcolor: '#e3f2fd',
                                                        color: '#1565c0'
                                                    }}
                                                />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                                    -
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                                {user.email}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    
                    {filteredAndSortedData.length === 0 && userAssessmentStatus.length > 0 && (
                        <Box sx={{ 
                            textAlign: 'center', 
                            py: 6,
                            background: 'linear-gradient(135deg, rgba(10, 0, 104, 0.02) 0%, rgba(204, 64, 73, 0.02) 100%)',
                            borderRadius: 2,
                            mt: 2,
                            border: '1px dashed rgba(10, 0, 104, 0.2)'
                        }}>
                            <Search sx={{ fontSize: 48, color: 'rgba(10, 0, 104, 0.3)', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#0a0068', mb: 1 }}>
                                No matching results
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                Try adjusting your search criteria or clear the search to see all users.
                            </Typography>
                            <Button 
                                size="small" 
                                variant="text" 
                                onClick={() => setSearchTerm('')}
                                sx={{ 
                                    mt: 2, 
                                    textTransform: 'none', 
                                    fontSize: '0.8rem',
                                    color: '#cc4049',
                                    '&:hover': {
                                        backgroundColor: 'rgba(204, 64, 73, 0.1)'
                                    }
                                }}
                            >
                                Clear Search
                            </Button>
                        </Box>
                    )}
                    
                    {userAssessmentStatus.length === 0 && (
                        <Box sx={{ 
                            textAlign: 'center', 
                            py: 6,
                            background: 'linear-gradient(135deg, rgba(10, 0, 104, 0.02) 0%, rgba(204, 64, 73, 0.02) 100%)',
                            borderRadius: 2,
                            mt: 2,
                            border: '1px dashed rgba(10, 0, 104, 0.2)'
                        }}>
                            <People sx={{ fontSize: 48, color: 'rgba(10, 0, 104, 0.3)', mb: 2 }} />
                            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, color: '#0a0068', mb: 1 }}>
                                No users found
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                                No users found for the selected program code.
                            </Typography>
                        </Box>
                    )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ 
                    p: 3, 
                    bgcolor: 'linear-gradient(135deg, rgba(10, 0, 104, 0.02) 0%, rgba(204, 64, 73, 0.02) 100%)',
                    borderTop: '1px solid rgba(10, 0, 104, 0.1)'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                            {searchTerm ? (
                                <>
                                    Filtered: {filteredAndSortedData.length} of {userAssessmentStatus.length} users | 
                                    Submitted: {filteredAndSortedData.filter(u => u.hasSubmitted).length} | 
                                    Pending: {filteredAndSortedData.filter(u => !u.hasSubmitted).length}
                                </>
                            ) : (
                                <>
                                    Total Users: {userAssessmentStatus.length} | 
                                    Submitted: {userAssessmentStatus.filter(u => u.hasSubmitted).length} | 
                                    Pending: {userAssessmentStatus.filter(u => !u.hasSubmitted).length}
                                </>
                            )}
                        </Typography>
                        <Button 
                            onClick={handleCloseDialog} 
                            variant="contained" 
                            sx={{ 
                                background: 'linear-gradient(135deg, #0a0068 0%, #cc4049 100%)',
                                color: 'white',
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                fontWeight: 600,
                                borderRadius: 2,
                                px: 3,
                                py: 1,
                                boxShadow: '0 2px 8px rgba(10, 0, 104, 0.3)',
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, rgba(10, 0, 104, 0.9) 0%, rgba(204, 64, 73, 0.9) 100%)',
                                    boxShadow: '0 4px 12px rgba(10, 0, 104, 0.4)',
                                    transform: 'translateY(-1px)'
                                }
                            }}>
                            Close
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AssessmentScoreCharts;